import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../Common/Configurations/APIConfig';
import AdminHeader from '../Common/Layout/AdminHeader';
import { rexTechTheme } from '../Common/Theme';
import { 
  Lock,
  Eye,
  EyeOff,
  CheckCircle,
  AlertCircle,
  Shield,
  Save
} from 'lucide-react';

const ChangePassword = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  const [errors, setErrors] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
 const userId = localStorage.getItem('userId');
  
  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // Clear messages when user starts typing
    setSuccessMessage('');
    setErrorMessage('');
  };

  // Toggle password visibility
  const togglePasswordVisibility = (field) => {
    switch (field) {
      case 'current':
        setShowCurrentPassword(!showCurrentPassword);
        break;
      case 'new':
        setShowNewPassword(!showNewPassword);
        break;
      case 'confirm':
        setShowConfirmPassword(!showConfirmPassword);
        break;
      default:
        break;
    }
  };

  // Validate form - Only basic required validation
  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    // Basic required validation only
    if (!formData.currentPassword.trim()) {
      newErrors.currentPassword = 'Current password is required';
      isValid = false;
    }

    if (!formData.newPassword.trim()) {
      newErrors.newPassword = 'New password is required';
      isValid = false;
    }

    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = 'Please confirm your new password';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setIsLoading(true);
      setErrorMessage('');
      setSuccessMessage('');

    
      // Prepare API request data
      const requestData = {
        UserId: userId,
        OldPassword: formData.currentPassword,
        NewPassword: formData.newPassword
      };

      // Make API call
      const response = await axios.post(
        config.APIACTIVEURL + config.CHANGEPASSWORD,
        requestData,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status === 200 || response.status === 201) {
        if (response.data === true) {
          setSuccessMessage('Password changed successfully!');
          
          // Reset form on success
          setFormData({
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
          });
          
          // Clear success message after 5 seconds
          setTimeout(() => {
            setSuccessMessage('');
          }, 5000);
        } else {
          setErrorMessage('Failed to change password. Please check your current password and try again.');
        }
      } else {
        setErrorMessage('Failed to change password. Please try again.');
      }
      
    } catch (error) {
      console.error('Error changing password:', error);
      
      if (error.response) {
        if (error.response.status === 400) {
          setErrorMessage('Invalid request. Please check your input.');
        } else if (error.response.status === 401) {
          setErrorMessage('Current password is incorrect.');
        } else {
          setErrorMessage(error.response.data?.message || 'Failed to change password. Please try again.');
        }
      } else if (error.request) {
        setErrorMessage('Network error. Please check your connection and try again.');
      } else {
        setErrorMessage('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Password input component
  const PasswordInput = ({ 
    label, 
    name, 
    value, 
    error, 
    showPassword, 
    onToggle, 
    onChange,
    placeholder 
  }) => (
    <div>
      <label style={{
        display: 'block',
        marginBottom: rexTechTheme.spacing.xs,
        color: rexTechTheme.colors.primary.dark,
        fontWeight: rexTechTheme.typography.fontWeight.semiBold,
        fontSize: rexTechTheme.typography.fontSize.regular
      }}>
        {label}
        <span style={{ color: rexTechTheme.colors.accent.orange, marginLeft: '2px' }}>*</span>
      </label>
      <div style={{ position: 'relative' }}>
        <input
          type={showPassword ? 'text' : 'password'}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          style={{
            width: '100%',
            padding: `${rexTechTheme.spacing.md} ${rexTechTheme.spacing.xxl} ${rexTechTheme.spacing.md} ${rexTechTheme.spacing.md}`,
            border: `2px solid ${error ? rexTechTheme.colors.accent.orange : rexTechTheme.colors.neutral.gray}`,
            borderRadius: rexTechTheme.borderRadius.medium,
            fontSize: rexTechTheme.typography.fontSize.regular,
            transition: 'all 0.2s ease',
            outline: 'none',
            boxSizing: 'border-box',
            ':focus': {
              borderColor: rexTechTheme.colors.primary.main,
              boxShadow: `0 0 0 3px ${rexTechTheme.colors.primary.lightest}`
            }
          }}
        />
        <button
          type="button"
          onClick={onToggle}
          style={{
            position: 'absolute',
            right: rexTechTheme.spacing.md,
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: rexTechTheme.colors.neutral.darkGray,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: rexTechTheme.spacing.xs,
            ':hover': {
              color: rexTechTheme.colors.primary.main
            }
          }}
        >
          {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
        </button>
      </div>
      {error && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: rexTechTheme.spacing.xs,
          marginTop: rexTechTheme.spacing.xs,
          color: rexTechTheme.colors.accent.orange,
          fontSize: rexTechTheme.typography.fontSize.small
        }}>
          <AlertCircle size={14} />
          <span>{error}</span>
        </div>
      )}
    </div>
  );

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: rexTechTheme.colors.neutral.lightGray,
      fontFamily: rexTechTheme.typography.fontFamily
    }}>
      <AdminHeader />
      
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: rexTechTheme.spacing.xl
      }}>
        {/* Header */}
        <div style={{
          marginBottom: rexTechTheme.spacing.xl
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: rexTechTheme.spacing.lg
          }}>
            <div>
              <h1 style={{
                fontSize: rexTechTheme.typography.fontSize.xxlarge,
                color: rexTechTheme.colors.primary.dark,
                fontWeight: rexTechTheme.typography.fontWeight.bold,
                marginBottom: rexTechTheme.spacing.xs
              }}>
                Change Password
              </h1>
              <p style={{
                color: rexTechTheme.colors.neutral.darkGray,
                fontSize: rexTechTheme.typography.fontSize.regular,
                margin: 0
              }}>
                Update your account password securely
              </p>
            </div>
            
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: rexTechTheme.spacing.sm,
              padding: rexTechTheme.spacing.md,
              backgroundColor: rexTechTheme.colors.primary.lightest,
              borderRadius: rexTechTheme.borderRadius.medium,
              border: `1px solid ${rexTechTheme.colors.primary.lighter}`
            }}>
              <Shield size={20} color={rexTechTheme.colors.primary.main} />
              <span style={{
                color: rexTechTheme.colors.primary.dark,
                fontWeight: rexTechTheme.typography.fontWeight.medium
              }}>
                Secure Password Update
              </span>
            </div>
          </div>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 2fr',
          gap: rexTechTheme.spacing.xl,
          '@media (max-width: 768px)': {
            gridTemplateColumns: '1fr'
          }
        }}>
          {/* Left Column: Security Info */}
          <div>
            <div style={{
              backgroundColor: rexTechTheme.colors.neutral.white,
              borderRadius: rexTechTheme.borderRadius.large,
              padding: rexTechTheme.spacing.xl,
              boxShadow: rexTechTheme.shadows.light,
              border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
              textAlign: 'center',
              marginBottom: rexTechTheme.spacing.lg
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                backgroundColor: rexTechTheme.colors.primary.lightest,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto',
                marginBottom: rexTechTheme.spacing.lg,
                border: `2px solid ${rexTechTheme.colors.primary.lighter}`
              }}>
                <Lock size={40} color={rexTechTheme.colors.primary.main} />
              </div>
              
              <h3 style={{
                color: rexTechTheme.colors.primary.dark,
                fontSize: rexTechTheme.typography.fontSize.xlarge,
                fontWeight: rexTechTheme.typography.fontWeight.bold,
                marginBottom: rexTechTheme.spacing.sm
              }}>
                Password Security
              </h3>
              
              <p style={{
                color: rexTechTheme.colors.neutral.darkGray,
                fontSize: rexTechTheme.typography.fontSize.small,
                lineHeight: 1.6,
                marginBottom: rexTechTheme.spacing.lg
              }}>
                Keep your account secure by creating a strong, unique password that you don't use elsewhere.
              </p>
            </div>
          </div>

          {/* Right Column: Change Password Form */}
          <div>
            <div style={{
              backgroundColor: rexTechTheme.colors.neutral.white,
              borderRadius: rexTechTheme.borderRadius.large,
              padding: rexTechTheme.spacing.xl,
              boxShadow: rexTechTheme.shadows.light,
              border: `1px solid ${rexTechTheme.colors.neutral.gray}`
            }}>
              <h3 style={{
                color: rexTechTheme.colors.primary.dark,
                fontSize: rexTechTheme.typography.fontSize.xlarge,
                fontWeight: rexTechTheme.typography.fontWeight.bold,
                marginBottom: rexTechTheme.spacing.lg,
                display: 'flex',
                alignItems: 'center',
                gap: rexTechTheme.spacing.sm
              }}>
                <Lock size={24} />
                Update Password
              </h3>
              
              {/* Status Messages */}
              {successMessage && (
                <div style={{
                  padding: rexTechTheme.spacing.md,
                  backgroundColor: rexTechTheme.colors.primary.lightest,
                  border: `1px solid ${rexTechTheme.colors.primary.light}`,
                  borderRadius: rexTechTheme.borderRadius.medium,
                  color: rexTechTheme.colors.primary.dark,
                  marginBottom: rexTechTheme.spacing.lg,
                  display: 'flex',
                  alignItems: 'center',
                  gap: rexTechTheme.spacing.sm
                }}>
                  <CheckCircle size={18} />
                  {successMessage}
                </div>
              )}
              
              {errorMessage && (
                <div style={{
                  padding: rexTechTheme.spacing.md,
                  backgroundColor: rexTechTheme.colors.accent.lightest,
                  border: `1px solid ${rexTechTheme.colors.accent.orange}`,
                  borderRadius: rexTechTheme.borderRadius.medium,
                  color: rexTechTheme.colors.accent.orange,
                  marginBottom: rexTechTheme.spacing.lg,
                  display: 'flex',
                  alignItems: 'center',
                  gap: rexTechTheme.spacing.sm
                }}>
                  <AlertCircle size={18} />
                  {errorMessage}
                </div>
              )}
              
              <form onSubmit={handleSubmit}>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: rexTechTheme.spacing.lg,
                  marginBottom: rexTechTheme.spacing.xl
                }}>
                  {/* Current Password */}
                  <PasswordInput
                    label="Current Password"
                    name="currentPassword"
                    value={formData.currentPassword}
                    error={errors.currentPassword}
                    showPassword={showCurrentPassword}
                    onToggle={() => togglePasswordVisibility('current')}
                    onChange={handleInputChange}
                    placeholder="Enter your current password"
                  />

                  {/* New Password */}
                  <PasswordInput
                    label="New Password"
                    name="newPassword"
                    value={formData.newPassword}
                    error={errors.newPassword}
                    showPassword={showNewPassword}
                    onToggle={() => togglePasswordVisibility('new')}
                    onChange={handleInputChange}
                    placeholder="Create a new password"
                  />

                  {/* Confirm Password */}
                  <PasswordInput
                    label="Confirm New Password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    error={errors.confirmPassword}
                    showPassword={showConfirmPassword}
                    onToggle={() => togglePasswordVisibility('confirm')}
                    onChange={handleInputChange}
                    placeholder="Re-enter your new password"
                  />
                </div>

                {/* Submit Button */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: rexTechTheme.spacing.md,
                  paddingTop: rexTechTheme.spacing.lg,
                  borderTop: `1px solid ${rexTechTheme.colors.neutral.gray}`
                }}>
                  <button
                    type="button"
                    onClick={() => navigate('/admin')}
                    style={{
                      padding: `${rexTechTheme.spacing.md} ${rexTechTheme.spacing.xl}`,
                      backgroundColor: rexTechTheme.colors.neutral.lightGray,
                      border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                      color: rexTechTheme.colors.neutral.darkGray,
                      borderRadius: rexTechTheme.borderRadius.medium,
                      fontSize: rexTechTheme.typography.fontSize.regular,
                      fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      ':hover': {
                        backgroundColor: rexTechTheme.colors.neutral.gray
                      }
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    style={{
                      padding: `${rexTechTheme.spacing.md} ${rexTechTheme.spacing.xl}`,
                      background: `linear-gradient(135deg, ${rexTechTheme.colors.primary.main} 0%, ${rexTechTheme.colors.primary.light} 100%)`,
                      color: rexTechTheme.colors.neutral.white,
                      border: 'none',
                      borderRadius: rexTechTheme.borderRadius.medium,
                      fontSize: rexTechTheme.typography.fontSize.regular,
                      fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: rexTechTheme.spacing.sm,
                      opacity: isLoading ? 0.7 : 1,
                      boxShadow: '0 4px 15px rgba(30, 132, 73, 0.3)',
                      ':hover': isLoading ? {} : {
                        background: `linear-gradient(135deg, ${rexTechTheme.colors.primary.dark} 0%, ${rexTechTheme.colors.primary.main} 100%)`,
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 20px rgba(30, 132, 73, 0.4)'
                      }
                    }}
                  >
                    {isLoading ? (
                      <>
                        <div style={{
                          width: '16px',
                          height: '16px',
                          border: `2px solid ${rexTechTheme.colors.neutral.white}`,
                          borderTopColor: 'transparent',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }} />
                        <style>{`
                          @keyframes spin {
                            0% { transform: rotate(0deg); }
                            100% { transform: rotate(360deg); }
                          }
                        `}</style>
                        Updating...
                      </>
                    ) : (
                      <>
                        <Save size={18} />
                        Update Password
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;